import { Link, useLocation } from "react-router-dom";
import { Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBrand, isRouteHidden } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * AntiGreed.app's own top bar — wordmark, the five primary sections, and the
 * light/dark toggle (which lives here instead of the floating DarkModeToggle
 * button Luigi's uses). AHP has AhpGlobalHeader; this is the sibling's, so
 * the brand reads as its own site rather than AHP's pages with a different
 * footer. Renders nothing on the other brands.
 */
export function AntiGreedHeader() {
  const brand = useBrand();
  const { isDark, toggle } = useTheme();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  if (brand.key !== "antigreed") return null;
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "1") {
    return null;
  }

  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
  const ink = isDark ? "#f5e6d3" : "#12200f";
  const muted = isDark ? "rgba(245,230,211,0.62)" : "rgba(18,32,15,0.62)";
  const orange = isDark ? "#ff7a2e" : "#c94a00";
  const rule = isDark ? "rgba(232,212,176,0.16)" : "rgba(18,32,15,0.12)";

  const links = [
    { to: "/search", label: t("nav.search", { defaultValue: "Search" }) },
    { to: "/scenarios", label: t("nav.scenarios", { defaultValue: "Scenarios" }) },
    { to: "/hospital-rankings", label: t("nav.ranks", { defaultValue: "Rankings" }) },
    { to: "/state-comparison", label: "States" },
    { to: "/near-me", label: "Near me" },
  ].filter((l) => !isRouteHidden(brand, l.to));

  const isActive = (to: string) => pathname === to || pathname.startsWith(to + "/");
  const label = isDark
    ? t("darkMode.switchToLight", { defaultValue: "Switch to light mode" })
    : t("darkMode.switchToDark", { defaultValue: "Switch to dark mode" });

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 9000,
        background: isDark ? "rgba(10,20,8,0.84)" : "rgba(247,241,230,0.88)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderBottom: `1px solid ${rule}`,
        fontFamily: sans,
      }}
    >
      <div className="mx-auto max-w-7xl px-4 h-14 flex items-center justify-between gap-4">
        <Link
          to="/"
          aria-label={brand.name}
          className="flex items-baseline"
          style={{ fontFamily: serif, fontSize: 24, lineHeight: 1, color: ink, textDecoration: "none", letterSpacing: "-0.01em" }}
        >
          <span>Anti</span>
          <span style={{ color: orange }}>Greed</span>
        </Link>

        <nav aria-label="Primary" className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const active = isActive(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                className="px-3 py-1.5 rounded-md text-sm transition-colors"
                style={{
                  color: active ? ink : muted,
                  fontWeight: active ? 600 : 500,
                  textDecoration: "none",
                  boxShadow: active ? `inset 0 -2px 0 ${orange}` : "none",
                }}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <button
          type="button"
          onClick={toggle}
          aria-label={label}
          title={label}
          className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
          style={{ color: ink, border: `1px solid ${rule}`, background: "transparent", cursor: "pointer" }}
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </div>
    </header>
  );
}

export default AntiGreedHeader;
